import {
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  type FormProps,
  Result,
  Select,
  Spin,
  theme,
  Typography,
} from 'antd';
import { Download, ExternalLink, Inbox } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { catchError, EMPTY, from, tap } from 'rxjs';
import SyncSuspense from '../../../components/sync-suspense';
import { type ProductionLine } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApi from '../../../hooks/use-secured-api';
import { useQuery } from '../../../jsonapi-provider';
import { LocalDate } from '../../../utils/local-date';

interface FiltersType {
  orderDate?: LocalDate;
  productionLine?: Array<ProductionLine['id']>;
}

const ProductionLinesReportPage = (): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [reportUrl, setReportUrl] = useState<string | undefined>('');
  const [fileName, setFileName] = useState<string>('');
  const api = useSecuredApi();

  const openInNewWindow = (): void => {
    window.open(`${reportUrl}#toolbar=0`);
  };

  const downloadReport = (): void => {
    if (!reportUrl) return;
    const link = document.createElement('a');
    link.href = reportUrl;
    link.download = fileName || 'production-line-report.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const productionLines = useQuery<ProductionLine[]>([
    'production-line',
    {
      fields: {
        'production-line': ['name'],
      },
      sort: 'name',
      page: {
        size: 10000,
      },
    },
  ]);

  const fetchSheet: FormProps<FiltersType>['onFinish'] = (submitted): void => {
    from(
      api.post(
        process.env.REACT_APP_PRODUCTION_LINE_ENDPOINT!,
        {
          orderDate: submitted.orderDate,
          productionLineIds: submitted.productionLine,
        },
        {},
        {},
        true
      )
    )
      .pipe(
        tap(({ blob }) => {
          const name = `Production Line - ${submitted.orderDate?.toISOString()}.pdf`;
          const fileURL = blob ? URL.createObjectURL(blob) : '';
          setReportUrl(fileURL);
          setFileName(name);
        }),
        catchError((error: Error) => {
          setReportUrl('');
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No production line reports available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
    setReportUrl(undefined);
  };

  return (
    <Flex gap="middle" vertical>
      <Flex gap="middle" style={{ width: '100%' }}>
        <Flex flex="1" vertical>
          <Card styles={{ body: { padding: `${token.paddingSM}px ${token.paddingXS}px` } }}>
            <SyncSuspense
              fallback={
                <Flex style={{ height: 300 }} justify="center" align="center">
                  <Spin />
                </Flex>
              }
              condition={!productionLines.isLoading}
            >
              <Flex gap="small" vertical>
                <Flex
                  style={{ paddingInline: token.paddingMD, paddingBlockStart: token.paddingSM }}
                  vertical
                >
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    Production Line Report
                  </Typography.Title>
                  <Typography.Text
                    style={{ marginBlockStart: token.marginXS, cursor: 'initial' }}
                    disabled
                  >
                    Generate and export production line reports based on filters
                  </Typography.Text>
                </Flex>
                <Form
                  layout="vertical"
                  onFinish={fetchSheet}
                  initialValues={{ orderDate: new LocalDate() }}
                  style={{ paddingInline: token.paddingMD, marginBlockStart: token.marginMD }}
                  colon={false}
                  requiredMark={false}
                >
                  <Form.Item<FiltersType>
                    name="orderDate"
                    label="Order date"
                    getValueProps={(value) => ({
                      value: value ? value.toDayjs() : null,
                    })}
                    normalize={(value) => (value ? new LocalDate(value) : null)}
                    rules={[{ required: true }]}
                  >
                    <DatePicker />
                  </Form.Item>

                  <Form.Item<FiltersType> name="productionLine" label="Production Line">
                    <Select
                      allowClear
                      showSearch
                      mode="multiple"
                      options={productionLines.data ?? []}
                      placeholder="All Production Lines"
                      fieldNames={{ label: 'name', value: 'id' }}
                    />
                  </Form.Item>

                  <Form.Item<FiltersType> style={{ marginBlockEnd: 0 }}>
                    <Flex
                      style={{
                        flexDirection: 'row-reverse',
                        marginBlockStart: token.marginMD,
                      }}
                    >
                      <Button
                        htmlType="submit"
                        type="primary"
                        style={{
                          marginInlineStart: 'auto',
                          paddingInline: token.paddingLG,
                        }}
                      >
                        Generate
                      </Button>
                    </Flex>
                  </Form.Item>
                </Form>
              </Flex>
            </SyncSuspense>
          </Card>
        </Flex>

        <Flex flex="1" style={{ maxWidth: 600 }} vertical>
          <Card
            styles={{
              body: reportUrl != null ? { padding: 0 } : undefined,
            }}
          >
            <Flex
              gap="small"
              style={{
                flexDirection: 'row-reverse',
                padding: token.paddingSM,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <Button
                block
                icon={<Download />}
                onClick={downloadReport}
                disabled={!reportUrl?.length}
              >
                Download
              </Button>
              <Button
                block
                icon={<ExternalLink />}
                onClick={openInNewWindow}
                disabled={!reportUrl?.length}
              >
                Open in new window
              </Button>
            </Flex>
            <SyncSuspense
              fallback={
                <Flex style={{ height: 300 }} justify="center" align="center">
                  <Spin />
                </Flex>
              }
              condition={reportUrl != null}
            >
              <Flex gap="small" justify="center" flex="1 1 100%" vertical>
                <SyncSuspense
                  fallback={
                    <Result
                      title={<Typography.Title level={4}>Nothing to preview</Typography.Title>}
                      icon={<Inbox />}
                      style={{ fontSize: '3em' }}
                    />
                  }
                  condition={!!reportUrl && reportUrl?.length}
                >
                  <iframe
                    src={`${reportUrl}#toolbar=0`}
                    style={{ border: 0, height: 'auto', aspectRatio: '210 / 297' }}
                    width="100%"
                  />
                </SyncSuspense>
              </Flex>
            </SyncSuspense>
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ProductionLinesReportPage;
